import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useSelector } from 'react-redux'
import useOdulOneriCall from '../../hooks/useOdulOneriCall'
import { BOARD_CRITERIA } from '../../helper/odulOneriCriteria'
import {
  SCORING_GROUP_ORDER,
  TOPIC_WEIGHT_PROFILES,
  criterionAverageFromVotes,
  formatWeightedScore,
  getActiveCriteriaForGroup,
  getActiveCriteriaForTopic,
  getMeetingWeightedScore,
  getScoringGroup,
  getTopicWeights,
} from '../../helper/odulOneriScoring'
import { BOARD_DECISION, BOARD_DECISION_LABELS } from '../../helper/odulOneriStatus'
import {
  formatDateDMY,
  formatEur,
  getImplementationMethodLabel,
  normalizeResultAttachments,
} from '../../helper/odulOneriImplementation'
import './KurulToplantisi.css'

const emptyVotes = () => BOARD_CRITERIA.map(() => [0, 0, 0, 0, 0, 0])

const voteRowSum = (row) => row.reduce((a, b) => a + (Number(b) || 0), 0)

const displayOrDash = (value) => {
  if (value === null || value === undefined || value === '') return '—'
  return value
}

/** Kurul KPI «Gerçekleşen» — OPEX doğrulanan sayısal, yoksa lider */
const resolveActualKpi = (realization = {}) =>
  realization.verifiedNumeric ||
  realization.leaderNumeric ||
  realization.verifiedQualitative ||
  realization.leaderQualitative ||
  realization.actualResult ||
  ''

const resolveActualCost = (realization = {}) => {
  if (realization.actualCost != null && realization.actualCost !== '') {
    return realization.actualCost
  }
  if (realization.leaderCost != null && realization.leaderCost !== '') {
    return realization.leaderCost
  }
  return null
}

/** Kurulun esas bakacağı kazanç: doğrulanan yıllık kazanç */
const resolveVerifiedGain = (realization = {}) => {
  if (realization.verifiedGain != null && realization.verifiedGain !== '') {
    return realization.verifiedGain
  }
  if (realization.leaderGain != null && realization.leaderGain !== '') {
    return realization.leaderGain
  }
  return null
}

const opexVerifyBadge = (impl) => {
  const result = impl?.opexVerification?.result
  if (result === 'verified') return { text: 'Kapatıldı — doğrulandı', className: 'ok' }
  if (result === 'returned') return { text: 'Düzeltme istendi', className: 'warn' }
  if (result === 'pending') return { text: 'Doğrulama bekliyor', className: 'warn' }
  return { text: '—', className: '' }
}

const KurulToplantisi = () => {
  const { currentUser } = useSelector((state) => state.auth)
  const { fetchAwardPool, saveBoardMeetingDecisions } = useOdulOneriCall()

  const [loading, setLoading] = useState(true)
  const [pool, setPool] = useState([])
  const [attendees, setAttendees] = useState('')
  const [items, setItems] = useState([])
  const [meetingNote, setMeetingNote] = useState('')
  const [meetingDate, setMeetingDate] = useState(() => new Date().toISOString().slice(0, 10))
  const [search, setSearch] = useState('')
  const [facilityFilter, setFacilityFilter] = useState('')
  const [activeGroup, setActiveGroup] = useState('')
  const [sortMode, setSortMode] = useState('id')
  const [quickCriterionId, setQuickCriterionId] = useState(BOARD_CRITERIA[0]?.id || '')
  const [matrixOpen, setMatrixOpen] = useState(false)
  const [saving, setSaving] = useState(false)
  const [clientError, setClientError] = useState('')

  const load = useCallback(async () => {
    setLoading(true)
    try {
      const list = await fetchAwardPool()
      setPool(list)
      setItems(
        list.map((r) => {
          const faydaAlani = r.implementation?.realization?.actualBenefitArea || ''
          return {
            recordId: r.id,
            implementationId: r.implementationId || r.implementation?.id || '',
            recordNumber: r.recordNumber,
            // Kurul grup/ağırlık anahtarı: OPEX'in seçtiği gerçekleşen fayda alanı
            topic: faydaAlani || r.topic,
            proposalTopic: r.topic,
            actualBenefitArea: faydaAlani,
            votes: emptyVotes(),
            result: BOARD_DECISION.THANKS,
            justification: '',
          }
        })
      )
    } finally {
      setLoading(false)
    }
  }, [fetchAwardPool])

  useEffect(() => {
    load()
  }, [load])

  const attendeeN = Number(attendees) || 0

  const enrichedItems = useMemo(() => {
    return items.map((item, idx) => {
      const record = pool[idx]
      const impl = record?.implementation
      const activeCriteria = getActiveCriteriaForTopic(item.topic)
      const activeIds = new Set(activeCriteria.map((c) => c.id))
      const averages = item.votes.map((row, ci) => {
        const criterion = BOARD_CRITERIA[ci]
        if (!activeIds.has(criterion.id)) return 0
        return criterionAverageFromVotes(row, attendeeN)
      })
      const groupScore = getMeetingWeightedScore(
        averages.map((avg, ci) => {
          const criterion = BOARD_CRITERIA[ci]
          if (!activeIds.has(criterion.id)) return 0
          return avg
        }),
        item.topic
      )
      // Only score when all active criteria have valid vote sums
      const allActiveReady =
        attendeeN > 0 &&
        activeCriteria.every((c) => {
          const ci = BOARD_CRITERIA.findIndex((x) => x.id === c.id)
          return ci >= 0 && voteRowSum(item.votes[ci]) === attendeeN
        })
      const weights = getTopicWeights(item.topic)
      const scoringGroup = getScoringGroup(item.topic)
      return {
        ...item,
        record,
        impl,
        averages,
        groupScore: allActiveReady ? groupScore : null,
        weights,
        scoringGroup,
        activeCriteria,
        allActiveReady,
      }
    })
  }, [items, pool, attendeeN])

  const facilities = useMemo(() => {
    const set = new Set()
    enrichedItems.forEach((item) => {
      const f = item.record?.location || item.record?.facility || ''
      if (f) set.add(f)
    })
    return [...set].sort((a, b) => a.localeCompare(b, 'tr'))
  }, [enrichedItems])

  const filteredItems = useMemo(() => {
    const q = search.trim().toLocaleLowerCase('tr-TR')
    return enrichedItems.filter((item) => {
      const facility = item.record?.location || item.record?.facility || ''
      if (facilityFilter && facility !== facilityFilter) return false
      if (!q) return true
      const hay = [
        item.recordNumber,
        item.topic,
        item.record?.fullName,
        item.impl?.plan?.workSummary,
        item.impl?.id,
      ]
        .filter(Boolean)
        .join(' ')
        .toLocaleLowerCase('tr-TR')
      return hay.includes(q)
    })
  }, [enrichedItems, search, facilityFilter])

  const groupTabs = useMemo(() => {
    const counts = {}
    filteredItems.forEach((item) => {
      const g = item.scoringGroup
      counts[g] = (counts[g] || 0) + 1
    })
    if (filteredItems.length === 0 && enrichedItems.length === 0) {
      return SCORING_GROUP_ORDER.map((g) => ({ name: g, count: 0 }))
    }
    const names = SCORING_GROUP_ORDER.filter((g) => counts[g] > 0)
    Object.keys(counts).forEach((g) => {
      if (!names.includes(g)) names.push(g)
    })
    return names.map((g) => ({ name: g, count: counts[g] || 0 }))
  }, [filteredItems, enrichedItems.length])

  useEffect(() => {
    if (!groupTabs.length) {
      setActiveGroup('')
      return
    }
    if (!activeGroup || !groupTabs.some((g) => g.name === activeGroup)) {
      setActiveGroup(groupTabs[0].name)
    }
  }, [groupTabs, activeGroup])

  const visibleItems = useMemo(() => {
    let list = filteredItems.filter((item) => item.scoringGroup === activeGroup)
    if (sortMode === 'score') {
      list = [...list].sort((a, b) => (b.groupScore || 0) - (a.groupScore || 0))
    } else {
      list = [...list].sort((a, b) =>
        String(a.recordNumber || '').localeCompare(String(b.recordNumber || ''), 'tr')
      )
    }
    return list
  }, [filteredItems, activeGroup, sortMode])

  const groupCriteria = useMemo(
    () => (activeGroup ? getActiveCriteriaForGroup(activeGroup) : BOARD_CRITERIA),
    [activeGroup]
  )

  useEffect(() => {
    if (groupCriteria.length && !groupCriteria.some((c) => c.id === quickCriterionId)) {
      setQuickCriterionId(groupCriteria[0].id)
    }
  }, [groupCriteria, quickCriterionId])

  const quickCriterion = BOARD_CRITERIA.find((c) => c.id === quickCriterionId) || groupCriteria[0]

  const stats = useMemo(() => {
    const scored = filteredItems.filter((i) => i.groupScore != null).length
    const decided = filteredItems.filter(
      (i) => i.result && String(i.justification || '').trim().length >= 8
    ).length
    return { count: filteredItems.length, scored, decided }
  }, [filteredItems])

  const setVote = (recordId, criterionIndex, scoreIndex, raw) => {
    const digits = String(raw ?? '').replace(/\D/g, '')
    const parsed = digits === '' ? 0 : Number(digits)
    if (!Number.isFinite(parsed) || parsed < 0) return

    setItems((prev) =>
      prev.map((item) => {
        if (item.recordId !== recordId) return item
        const votes = item.votes.map((row, ci) => {
          if (ci !== criterionIndex) return row
          const next = [...row]
          const oldVal = Number(next[scoreIndex]) || 0
          const others = voteRowSum(next) - oldVal
          // Kriter satırı toplamı katılan üye sayısını aşamaz
          const maxForCell =
            attendeeN > 0 ? Math.max(0, attendeeN - others) : parsed
          next[scoreIndex] = Math.min(parsed, maxForCell)
          return next
        })
        return { ...item, votes }
      })
    )
  }

  const setItemField = (recordId, field, value) => {
    setItems((prev) =>
      prev.map((item) => (item.recordId === recordId ? { ...item, [field]: value } : item))
    )
  }

  const handleSave = async () => {
    if (!attendeeN || attendeeN < 1) {
      setClientError('Katılan üye sayısı en az 1 olmalıdır.')
      return
    }
    for (const item of items) {
      const active = getActiveCriteriaForTopic(item.topic)
      for (const c of active) {
        const ci = BOARD_CRITERIA.findIndex((x) => x.id === c.id)
        if (ci < 0 || voteRowSum(item.votes[ci]) !== attendeeN) {
          setClientError(
            `${item.recordNumber}: «${c.title}» kriterinde oy toplamı katılan üye sayısına eşit olmalı.`
          )
          return
        }
      }
      if (!String(item.justification || '').trim() || String(item.justification).trim().length < 8) {
        setClientError(`${item.recordNumber}: karar gerekçesi en az 8 karakter olmalı.`)
        return
      }
    }
    setClientError('')
    setSaving(true)
    try {
      await saveBoardMeetingDecisions({
        attendees: attendeeN,
        meetingDate,
        items: items.map(
          ({ recordId, implementationId, recordNumber, topic, votes, result, justification }) => ({
            recordId,
            implementationId,
            recordNumber,
            topic,
            votes,
            result,
            justification,
          })
        ),
        meetingNote: meetingNote.trim(),
        actor: currentUser,
      })
      await load()
    } catch (_) {
      /* toast */
    } finally {
      setSaving(false)
    }
  }

  const voteInfo = (item, criterion) => {
    const ci = BOARD_CRITERIA.findIndex((c) => c.id === criterion.id)
    if (ci < 0) return { text: '', bad: true }
    const row = item.votes[ci]
    const total = voteRowSum(row)
    if (!attendeeN) return { text: 'Önce katılan üye sayısını girin.', bad: true }
    if (total === attendeeN) {
      const mean = item.averages[ci]
      const weight = Number(item.weights[criterion.id]) || 0
      const weightSum = item.activeCriteria.reduce((a, c) => a + c.weight, 0) || 100
      const contribution =
        mean != null ? ((mean / 5) * 100 * weight) / weightSum : null
      return {
        text: `Oy: ${total}/${attendeeN} • Ort: ${
          mean != null ? mean.toFixed(2) : '—'
        }/5 • Ağırlıklı: ${
          contribution != null ? contribution.toFixed(1) : '—'
        }`,
        bad: false,
      }
    }
    return {
      text:
        total > attendeeN
          ? `Oy: ${total}/${attendeeN} • Fazla oy girdiniz`
          : `Oy: ${total}/${attendeeN} • ${attendeeN - total} oy eksik`,
      bad: true,
    }
  }

  return (
    <div className="odul-kurul">
      <header className="odul-kurul-header">
        <div>
          <small>BONNA • OPEX</small>
          <h1>Kurul Toplantısı ve Karşılaştırmalı Puanlama</h1>
          <p>Ödül havuzundaki ve kurul değerlendirmesine hazır uygulamalar</p>
        </div>
        <span className="odul-kurul-badge">TOPLANTI GÜNÜ • EL KALDIRARAK OYLAMA</span>
      </header>

      <div className="odul-kurul-main">
        <section className="attendance">
          <div>
            <h2>1. Toplantı Katılımı</h2>
            <p>Önce toplantıya fiilen katılan, oy kullanacak üye sayısını girin.</p>
          </div>
          <label>
            Katılan üye sayısı
            <input
              className="attendees-input"
              type="number"
              min={1}
              placeholder="Örn. 7"
              value={attendees}
              onChange={(e) => setAttendees(e.target.value)}
            />
          </label>
          <span className="att-status">
            {attendeeN > 0
              ? `${attendeeN} kişi • Her aktif kriterde ${attendeeN} oy girilmeli`
              : 'Üye sayısı bekleniyor'}
          </span>
        </section>

        <section className="toolbar">
          <label>
            Toplantı tarihi
            <input
              type="date"
              value={meetingDate}
              onChange={(e) => setMeetingDate(e.target.value)}
            />
          </label>
          <label>
            Tesis
            <select
              value={facilityFilter}
              onChange={(e) => setFacilityFilter(e.target.value)}
            >
              <option value="">Tüm tesisler</option>
              {facilities.map((f) => (
                <option key={f} value={f}>
                  {f}
                </option>
              ))}
            </select>
          </label>
          <label>
            Öneri / kişi / uygulama ara
            <input
              type="search"
              placeholder="Öneri no, başlık veya kişi"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              style={{ minWidth: 220 }}
            />
          </label>
          <label style={{ minWidth: 280 }}>
            Toplantı notu (opsiyonel)
            <textarea
              rows={1}
              value={meetingNote}
              onChange={(e) => setMeetingNote(e.target.value)}
              placeholder="Toplantı notu"
            />
          </label>
          <div className="push">
            <button type="button" onClick={() => setMatrixOpen(true)}>
              Puanlama Matrisini Aç
            </button>
            <button type="button" onClick={load}>
              Yenile
            </button>
          </div>
        </section>

        <section className="stats">
          <div>
            <b>{stats.count}</b>
            <span>Havuzdaki uygulama</span>
          </div>
          <div>
            <b>{stats.scored}</b>
            <span>Puanlaması tamamlanan</span>
          </div>
          <div>
            <b>{stats.decided}</b>
            <span>Kararı girilen</span>
          </div>
          <div className="source-note">
            El kaldırma oyları: her aktif kriterde 0–5 puan için verilen oy sayıları toplamı,
            katılan üye sayısına eşit olmalıdır. Nihai karar kurulundur; sistem otomatik ödül
            vermez.
          </div>
        </section>

        <nav className="groups" aria-label="Puanlama grupları">
          {groupTabs.map((g) => (
            <button
              key={g.name}
              type="button"
              className={`group ${g.name === activeGroup ? 'active' : ''}`}
              onClick={() => setActiveGroup(g.name)}
            >
              {g.name}
              <small>{g.count} uygulama</small>
            </button>
          ))}
        </nav>

        <section className="panel">
          <div className="panel-head">
            <div>
              <h2>{activeGroup || 'Gösterilecek uygulama yok'}</h2>
              <div className="sub">
                {activeGroup
                  ? `Gerçekleşen fayda alanına göre karşılaştırma • ${visibleItems.length} uygulama • ${BOARD_CRITERIA.length} kriter (kapsam dışı oylanmaz)`
                  : ''}
              </div>
            </div>
            <label className="sub">
              Sıralama{' '}
              <select value={sortMode} onChange={(e) => setSortMode(e.target.value)}>
                <option value="id">Öneri numarası</option>
                <option value="score">Grup içi puan: yüksekten düşüğe</option>
              </select>
            </label>
          </div>

          <details className="settings">
            <summary>Konu / Grup / Kriter Ağırlıkları • Sabit Puanlama Tablosu</summary>
            <div className="policy-wrap">
              <table className="policy-table">
                <thead>
                  <tr>
                    <th>Öneri / Gerçekleşen Fayda Konusu</th>
                    <th>Grup</th>
                    {BOARD_CRITERIA.map((c) => (
                      <th key={c.id}>{c.title}</th>
                    ))}
                    <th>Toplam</th>
                  </tr>
                </thead>
                <tbody>
                  {Object.entries(TOPIC_WEIGHT_PROFILES).map(([topic, w]) => (
                    <tr key={topic}>
                      <td>{topic}</td>
                      <td>{getScoringGroup(topic)}</td>
                      {BOARD_CRITERIA.map((c) => (
                        <td key={c.id}>{w[c.id] ? `%${w[c.id]}` : '—'}</td>
                      ))}
                      <td>%100</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="notice">
              Ağırlıklar konuya sabittir. Ağırlığı %0 olan kriterler o satırda kapsam dışıdır.
              Nihai ödül / teşekkür / erteleme kararı kurul üyelerinin el kaldırma oyları ve
              gerekçesi ile verilir.
            </div>
          </details>

          <div className="formula">
            <strong>Hesap:</strong> Kriter ortalaması = Σ(puan × oy) / katılan üye. Nihai /100 =
            Σ((kriterOrt / 5) × konu ağırlığı%). Oy toplamı her aktif kriterde katılan üye
            sayısına eşit olmalıdır.
          </div>

          <details className="quick-guide" open>
            <summary>Kısa Puanlama Rehberi • 0–5 puan açıklamaları</summary>
            <div className="quick-top">
              <label>
                Kriter{' '}
                <select
                  value={quickCriterionId}
                  onChange={(e) => setQuickCriterionId(e.target.value)}
                >
                  {groupCriteria.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.title}
                    </option>
                  ))}
                </select>
              </label>
              <span className="sub">
                Kriter ağırlığı: konuya göre satırda gösterilir
              </span>
            </div>
            {quickCriterion && (
              <>
                <p className="sub" style={{ marginTop: 8 }}>
                  {quickCriterion.question}
                </p>
                <div className="quick-levels">
                  {(quickCriterion.levels || []).map((level, i) => (
                    <div key={i}>
                      <b>{i} puan</b>
                      {level}
                    </div>
                  ))}
                </div>
              </>
            )}
          </details>

          {clientError && <div className="client-error">{clientError}</div>}

          {loading && <div className="empty">Yükleniyor…</div>}
          {!loading && visibleItems.length === 0 && (
            <div className="empty">
              {enrichedItems.length === 0
                ? 'Ödül havuzunda (veya ertelenmiş) kayıt bulunmuyor.'
                : 'Bu grup / filtrede uygulama yok.'}
            </div>
          )}

          {!loading && visibleItems.length > 0 && (
            <div className="table-wrap">
              <table className="compare">
                <colgroup>
                  <col style={{ width: 200 }} />
                  <col style={{ width: 150 }} />
                  <col style={{ width: 220 }} />
                  <col style={{ width: 210 }} />
                  <col style={{ width: 175 }} />
                  <col style={{ width: 170 }} />
                  {BOARD_CRITERIA.map((c) => (
                    <col key={c.id} style={{ width: 185 }} />
                  ))}
                  <col style={{ width: 200 }} />
                </colgroup>
                <thead>
                  <tr>
                    <th>Öneri / Öneri Sahibi</th>
                    <th>Öneri Konusu</th>
                    <th>Uygulama / Yapılan Çalışma</th>
                    <th>
                      KPI • Mevcut / Hedef / Gerçekleşen
                    </th>
                    <th>Maliyet ve Kazanç</th>
                    <th>OPEX Doğrulaması / Ekler</th>
                    {BOARD_CRITERIA.map((c) => (
                      <th key={c.id}>
                        {c.title}
                        <small>Her puana verilen oy sayısı</small>
                      </th>
                    ))}
                    <th>Grup Puanı / Kurul Kararı</th>
                  </tr>
                </thead>
                <tbody>
                  {visibleItems.map((item) => {
                    const benefit = item.impl?.benefit || {}
                    const realization = item.impl?.realization || {}
                    const plan = item.impl?.plan || {}
                    const opex = item.impl?.opexVerification || {}
                    const activeIds = new Set(item.activeCriteria.map((c) => c.id))
                    const actualKpi = resolveActualKpi(realization)
                    const actualCost = resolveActualCost(realization)
                    const verifiedGain = resolveVerifiedGain(realization)
                    const verifyBadge = opexVerifyBadge(item.impl)
                    const applicantName =
                      item.record?.applicant?.fullName ||
                      item.record?.fullName ||
                      '—'
                    const applicantMeta = [
                      item.record?.relatedDepartmentName ||
                        item.record?.employeeDept,
                      item.record?.applicant?.location || item.record?.location,
                    ]
                      .filter(Boolean)
                      .join(' • ')
                    const evidenceText =
                      opex.evidence ||
                      realization.evidence ||
                      realization.leaderEvidence ||
                      ''
                    const resultAttachments = normalizeResultAttachments(
                      opex.resultAttachments || realization.resultAttachments
                    )
                    return (
                      <tr key={item.recordId}>
                        <td>
                          <strong>
                            {item.proposalTopic ||
                              item.record?.topic ||
                              item.topic ||
                              '—'}
                          </strong>
                          <span className="minor">{item.recordNumber}</span>
                          <p style={{ margin: '5px 0 8px' }}>{applicantName}</p>
                          <span className="minor">{applicantMeta || '—'}</span>
                          <div className="proposal-detail">
                            <small>Öneri detayı</small>
                            <p>
                              {item.record?.proposedState ||
                                item.impl?.proposedState ||
                                '—'}
                            </p>
                          </div>
                        </td>
                        <td>
                          <div className="topic">{item.topic}</div>
                          {item.actualBenefitArea &&
                            item.proposalTopic &&
                            item.actualBenefitArea !== item.proposalTopic && (
                              <span className="minor">
                                Öneri konusu: {item.proposalTopic}
                              </span>
                            )}
                          <span className="minor">Grup: {item.scoringGroup}</span>
                        </td>
                        <td>
                          {(plan.method || item.impl?.method) && (
                            <span className="badge">
                              {getImplementationMethodLabel(
                                plan.method || item.impl?.method
                              ) || plan.method}
                            </span>
                          )}
                          <span className="minor">
                            {item.impl?.uygulamaNo || item.implementationId || '—'}
                          </span>
                          <p style={{ margin: '8px 0' }}>
                            {plan.workSummary ||
                              realization.leaderResult ||
                              realization.actualResult ||
                              '—'}
                          </p>
                        </td>
                        <td>
                          <div className="kpi-name">
                            {displayOrDash(benefit.kpi || benefit.type)}
                          </div>
                          <div className="kpi-grid">
                            <div>
                              <small>Mevcut</small>
                              <b>{displayOrDash(benefit.baseline)}</b>
                            </div>
                            <div>
                              <small>Hedef</small>
                              <b>{displayOrDash(benefit.target)}</b>
                            </div>
                            <div>
                              <small>Gerçekleşen</small>
                              <b>{displayOrDash(actualKpi)}</b>
                            </div>
                          </div>
                        </td>
                        <td>
                          <div className="finance finance-stack">
                            <span>Planlanan maliyet</span>
                            <b>{formatEur(benefit.estimatedCost)}</b>
                            <span>Fiili maliyet</span>
                            <b>{formatEur(actualCost)}</b>
                            <span>Beklenen yıllık kazanç</span>
                            <b className="muted-gain">
                              {formatEur(benefit.estimatedGain)}
                            </b>
                            <span className="verified-label">
                              Doğrulanan yıllık kazanç
                            </span>
                            <b className="verified-gain">
                              {formatEur(verifiedGain)}
                            </b>
                          </div>
                        </td>
                        <td>
                          <span
                            className={`verify-badge${
                              verifyBadge.className ? ` ${verifyBadge.className}` : ''
                            }`}
                          >
                            {verifyBadge.text}
                          </span>
                          <div className="verify-meta">
                            <span className="minor">
                              Tarih:{' '}
                              {formatDateDMY(
                                opex.verifyDate ||
                                  (opex.verifiedAt
                                    ? String(opex.verifiedAt).slice(0, 10)
                                    : '')
                              )}
                            </span>
                            <span className="minor">
                              Birim: {displayOrDash(opex.verifier || realization.verifier)}
                            </span>
                            <span className="minor">
                              Yöntem:{' '}
                              {displayOrDash(
                                opex.verifyMethod || realization.verifyMethod
                              )}
                            </span>
                            {evidenceText ? (
                              <span className="minor evidence">
                                Kanıt: {evidenceText}
                              </span>
                            ) : null}
                            <details className="docs">
                              <summary>
                                Sonuç ekleri ({resultAttachments.length})
                              </summary>
                              {resultAttachments.length ? (
                                resultAttachments.map((f) => (
                                  <a
                                    key={f.id || f.url || f.name}
                                    href={f.url || f.dataUrl}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    download={f.name}
                                  >
                                    {f.name}
                                  </a>
                                ))
                              ) : (
                                <span className="minor">Ek dosya bulunmuyor.</span>
                              )}
                            </details>
                          </div>
                        </td>
                        {BOARD_CRITERIA.map((c) => {
                          const ci = BOARD_CRITERIA.findIndex((x) => x.id === c.id)
                          const inScope = activeIds.has(c.id)
                          const weight = Number(item.weights[c.id]) || 0
                          if (!inScope) {
                            return (
                              <td key={c.id} className="out-of-scope">
                                <span className="minor">Kapsam dışı</span>
                                <span className="minor">Ağırlık: %0</span>
                              </td>
                            )
                          }
                          const info = voteInfo(item, c)
                          return (
                            <td key={c.id}>
                              <span className="row-weight">Ağırlık: %{weight}</span>
                              <div className="vote-grid">
                                {[0, 1, 2, 3, 4, 5].map((score) => {
                                  const cellVal = Number(item.votes[ci][score]) || 0
                                  const others = voteRowSum(item.votes[ci]) - cellVal
                                  const maxForCell = Math.max(0, attendeeN - others)
                                  return (
                                    <label key={score}>
                                      {score} puan
                                      <input
                                        className="vote-count"
                                        type="text"
                                        inputMode="numeric"
                                        autoComplete="off"
                                        disabled={!attendeeN}
                                        placeholder="0"
                                        maxLength={String(attendeeN || 0).length || 1}
                                        value={cellVal === 0 ? '' : String(cellVal)}
                                        onChange={(e) =>
                                          setVote(
                                            item.recordId,
                                            ci,
                                            score,
                                            e.target.value
                                          )
                                        }
                                        title={
                                          attendeeN
                                            ? `Bu kutuya en fazla ${maxForCell} (satır toplamı ≤ ${attendeeN})`
                                            : ''
                                        }
                                      />
                                    </label>
                                  )
                                })}
                              </div>
                              <div className={`vote-info${info.bad ? ' bad' : ''}`}>
                                {info.text}
                              </div>
                            </td>
                          )
                        })}
                        <td>
                          <div className="total">
                            {item.groupScore != null
                              ? formatWeightedScore(item.groupScore)
                              : '—'}
                            <span className="minor">/ 100</span>
                          </div>
                          <div className="outcome">
                            {item.groupScore != null
                              ? 'Puanlama tamam • Nihai karar kurulundur'
                              : 'Puanlama bekleniyor • Nihai karar kurulundur'}
                          </div>
                          <select
                            className="decision"
                            value={item.result}
                            onChange={(e) =>
                              setItemField(item.recordId, 'result', e.target.value)
                            }
                          >
                            {Object.values(BOARD_DECISION).map((d) => (
                              <option key={d} value={d}>
                                {BOARD_DECISION_LABELS[d]}
                              </option>
                            ))}
                          </select>
                          <textarea
                            className="reason"
                            placeholder="Karar gerekçesi (min. 8 karakter)"
                            value={item.justification}
                            onChange={(e) =>
                              setItemField(item.recordId, 'justification', e.target.value)
                            }
                          />
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>
          )}

          <div className="panel-footer">
            <span className="sub">
              Her aktif kriterde oy sayıları toplamı katılan üye sayısına eşit olmalıdır.
            </span>
            <button
              type="button"
              className="primary"
              disabled={saving || !attendeeN || items.length === 0}
              onClick={handleSave}
            >
              {saving ? 'Kaydediliyor…' : 'Toplantı Kararlarını Kaydet'}
            </button>
          </div>
        </section>
      </div>

      <div
        className={`modal${matrixOpen ? ' open' : ''}`}
        role="dialog"
        aria-modal="true"
        onClick={(e) => {
          if (e.target === e.currentTarget) setMatrixOpen(false)
        }}
      >
        <div className="dialog">
          <button type="button" className="close" onClick={() => setMatrixOpen(false)}>
            Kapat
          </button>
          <h2 style={{ marginTop: 0 }}>Mevcut Puanlama Matrisi</h2>
          <p className="sub">
            {activeGroup || 'Grup'} • Aktif kriterler ve 0–5 puan tanımları
          </p>
          <table>
            <thead>
              <tr>
                <th>Kriter</th>
                <th>Soru</th>
                {[0, 1, 2, 3, 4, 5].map((n) => (
                  <th key={n}>{n} puan</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {groupCriteria.map((c) => (
                <tr key={c.id}>
                  <td>
                    <strong>{c.title}</strong>
                  </td>
                  <td>{c.question}</td>
                  {(c.levels || []).map((level, i) => (
                    <td key={i}>{level}</td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  )
}

export default KurulToplantisi
